import './TeamMembers.scss';
import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProtectedInterval } from '../../hooks/useProtectedInterval';
import Pagination from '../../components/Layout/Pagination';
import EmptyState from '../../components/Layout/EmptyState';

export default function TeamMembers() {
    const { t } = useTranslation('layout');
    const api_url = useSelector((state) => state.API_URL);
    const theme = useSelector((state) => state.APP_THEME);
    const navigate = useNavigate();

    const [members, setMembers] = useState([]);
    const [teamleadBalance, setTeamleadBalance] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all'); // all, active, frozen
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const fetchMembers = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        if (!jwtToken) return;

        try {
            const response = await fetch(api_url + 'teamlead/members/', {
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                    'Content-Type': 'application/json',
                },
            });
            if (response.ok) {
                const data = await response.json();
                setMembers(data.members || []);
                setTeamleadBalance(data.teamlead_balance || 0);
            }
        } catch (e) {
            console.error('Failed to fetch members:', e);
        } finally {
            setLoading(false);
        }
    }, [api_url]);

    useProtectedInterval(fetchMembers, [api_url]);

    // Фильтрация и поиск
    const filtered = members.filter(m => {
        if (filter === 'active' && (m.is_frozen || !m.is_active)) return false;
        if (filter === 'frozen' && !m.is_frozen) return false;
        if (search) {
            const s = search.toLowerCase();
            return m.username.toLowerCase().includes(s) ||
                   (m.first_name && m.first_name.toLowerCase().includes(s));
        }
        return true;
    });

    // Пагинация
    const totalCount = filtered.length;
    const paged = filtered.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

    const getStatusBadge = (member) => {
        if (member.is_frozen) {
            return <span className="TeamMembers__badge TeamMembers__badge--frozen">{member.freeze_kind || 'Заморожен'}</span>;
        }
        if (!member.is_active) {
            return <span className="TeamMembers__badge TeamMembers__badge--inactive">Неактивен</span>;
        }
        if (member.active_creds > 0) {
            return <span className="TeamMembers__badge TeamMembers__badge--working">В работе</span>;
        }
        return <span className="TeamMembers__badge TeamMembers__badge--idle">Не в работе</span>;
    };

    if (loading) {
        return <div style={{ padding: '24px' }}><h2>{t('sidebar.menu.team')}</h2><p>Загрузка...</p></div>;
    }

    return (
        <div className="TeamMembers">
            <div className="TeamMembers__header">
                <h2>{t('sidebar.menu.team')}</h2>
                <div className="TeamMembers__summary">
                    <span className="TeamMembers__summary-item">
                        Баланс команды: <b>{members.reduce((s, m) => s + m.balance_usdt, 0).toFixed(2)} $</b>
                    </span>
                    <span className="TeamMembers__summary-item">
                        Ваш баланс: <b>{teamleadBalance} $</b>
                    </span>
                    <span className="TeamMembers__summary-item">
                        Трейдеров: <b>{members.length}</b>
                    </span>
                </div>
            </div>

            {/* Фильтры */}
            <div className="TeamMembers__toolbar">
                <input
                    className="TeamMembers__search"
                    type="text"
                    placeholder="Поиск по имени..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPageIndex(0); }}
                />
                <div className="TeamMembers__filters">
                    {['all', 'active', 'frozen'].map(f => (
                        <button
                            key={f}
                            className={`TeamMembers__filter-btn${filter === f ? ' TeamMembers__filter-btn--active' : ''}`}
                            onClick={() => { setFilter(f); setPageIndex(0); }}
                        >
                            {f === 'all' ? 'Все' : f === 'active' ? 'Активные' : 'Замороженные'}
                        </button>
                    ))}
                </div>
            </div>

            <Pagination
                pageIndex={pageIndex}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                pageSizes={[5, 10, 20]}
            />

            {paged.length === 0 ? (
                <EmptyState message={search ? 'Ничего не найдено' : 'Нет трейдеров в команде'} />
            ) : (
                <div className="TeamMembers__table-wrap">
                    <table className="TeamMembers__table">
                        <thead>
                            <tr>
                                <th>Username</th>
                                <th>Имя</th>
                                <th>Баланс</th>
                                <th>Статус</th>
                                <th>Реквизиты</th>
                                <th>Заявки</th>
                                <th>Бот</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paged.map(m => (
                                <tr key={m.id} className={`TeamMembers__row${m.is_frozen ? ' TeamMembers__row--frozen' : ''}`} onClick={() => navigate(`/team/${m.id}`)} style={{cursor: 'pointer'}}>
                                    <td className="TeamMembers__username">{m.username}</td>
                                    <td>{m.first_name} {m.last_name}</td>
                                    <td className={`TeamMembers__balance${m.balance_usdt < 0 ? ' TeamMembers__balance--negative' : ''}`}>
                                        {m.balance_usdt.toFixed(2)} $
                                    </td>
                                    <td>{getStatusBadge(m)}</td>
                                    <td>
                                        <span className="TeamMembers__creds">
                                            {m.active_creds} / {m.total_creds}
                                        </span>
                                    </td>
                                    <td>
                                        {m.active_deals > 0 ? (
                                            <span className="TeamMembers__deals-active">{m.active_deals}</span>
                                        ) : (
                                            <span className="TeamMembers__deals-none">0</span>
                                        )}
                                    </td>
                                    <td>
                                        {m.has_chat ? (
                                            <span className="TeamMembers__bot-ok" title="Подключён">✓</span>
                                        ) : (
                                            <span className="TeamMembers__bot-no" title="Не подключён">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Pagination
                pageIndex={pageIndex}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                pageSizes={[5, 10, 20]}
            />
        </div>
    );
}
